import React, {useEffect, useState} from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import ResultInfo from '../ResultInfo/ResultInfo.jsx';
import {Oval} from 'react-loader-spinner';
import EventList from '../EventList/EventList.jsx';
import {FormInput} from '../FormInput/FormInput.jsx';
import {FormGroup} from '../FormGroup/FormGroup.jsx';

const compareDates = (startDate, endDate, date) => {
    const standardizeDate = (dateString) => new Date(dateString).toISOString().split('T')[0];

    const standardizedStartDate = standardizeDate(`${startDate}T00:00:00`);
    // Check if there is an endDate by comparing to Unix Timestamp 0 value
    const standardizedEndDate = endDate && new Date(endDate).getTime() !== 0 ? standardizeDate(`${endDate}T00:00:00`) : null;
    const standardizedDate = standardizeDate(date);

    if (!standardizedEndDate) return standardizedStartDate === standardizedDate;

    return standardizedStartDate <= standardizedDate && standardizedEndDate >= standardizedDate;
};

const isEventFinished = (startDate, endDate) => {
    if (new Date(endDate).getTime() === 0) return new Date(`${startDate}T00:00:00`).getTime() < new Date().getTime();
    return new Date(`${endDate}T00:00:00`).getTime() < new Date().getTime();
};

const eventMatchesFilter = (event, status) => {
    if (status === eventStatusDict.wszystkie) return true;
    const isUpcoming = !isEventFinished(event.startDate, event.endDate);
    return status === eventStatusDict.nadchodzace ? isUpcoming : !isUpcoming;
};

const eventStatusDict = {
    wszystkie: 'Wszystkie',
    nadchodzace: 'Nadchodzące',
    zakonczone: 'Zakończone',
};
const eventTags = ['Wszystkie', 'Wykład otwarty', 'Konferencja', 'Debata', 'Spotkanie', 'Koncert', 'Wystawa', 'Szkolenie'];

const targetGroupsDict = {
    wszyscy: 'Dla wszystkich',
    kandydaci: 'Kandydaci',
    studenci: 'Studenci',
    doktoranci: 'Doktoranci',
    absolwenci: 'Absolwenci',
    pracownicy: 'Pracownicy',
    szkoly: 'Szkoły',
};

export const Main = () => {
    const [events, setEvents] = useState([]);
    const [visibleEvents, setVisibleEvents] = useState([]);
    const [currentDate, setCurrentDate] = useState(null);
    const [currentTag, setCurrentTag] = useState('Wszystkie');
    const [currentStatus, setCurrentStatus] = useState('Nadchodzące');
    const [pages, setPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [currentGroup, setCurrentGroup] = useState([]);
    const [darkMode, setDarkMode] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            const response = await fetch(`https://amu.edu.pl/__data/assets/js_file/0027/545409/eventsJS.js/_nocache`);
            const {data} = await response.json();
            data.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

            data.forEach((element) => {
                const elementGroups = element.targetGroup ? element.targetGroup.split('; ') : [];
                elementGroups.forEach((group, index) => {
                    elementGroups[index] = targetGroupsDict[group] || group;
                });
                element.targetGroup = elementGroups;
            });

            // Filter events to show only upcoming events by default
            const filteredEvents = data.filter((event) => eventMatchesFilter(event, currentStatus));

            setPages(filteredEvents.length / 20);
            setEvents(data);
            setVisibleEvents(filteredEvents);
            setLoading(false);
        };

        fetchEvents();
    }, []);

    useEffect(() => {
        if (document.cookie.includes('high-contrast=1')) {
            setDarkMode(true);
        }
    }, []);

    useEffect(() => {
        const filteredEvents = events
            .filter((event) => currentTag === 'Wszystkie' || event.tag === currentTag)
            .filter(
                (event) =>
                    currentGroup.length === 0 ||
                    currentGroup.some((item) => {
                        return event.targetGroup.includes(targetGroupsDict[item]);
                    }),
            )
            .filter((event) => !currentDate || compareDates(event.startDate, event.endDate, currentDate))
            .filter(
                (event) =>
                    currentStatus === eventStatusDict.wszystkie ||
                    (currentStatus === eventStatusDict.nadchodzace ? !isEventFinished(event.startDate, event.endDate) : isEventFinished(event.startDate, event.endDate)),
            )
            .sort((a, b) => (currentStatus !== eventStatusDict.nadchodzace ? new Date(b.startDate) - new Date(a.startDate) : new Date(a.startDate) - new Date(b.startDate)));

        setVisibleEvents(filteredEvents);
        setPages(filteredEvents.length / 20);
        setCurrentPage(1);
    }, [currentTag, currentStatus, currentDate, currentGroup]);

    /**
     * Returns class name for given date in calendar
     * @param {Date} date
     * @returns {string | null}
     */
    const printCalendarClassName = ({date}) => {
        events.some((event) => {
            return compareDates(event.startDate, event.endDate, date);
        });
        if (events.some((event) => compareDates(event.startDate, event.endDate, date))) {
            // eslint-disable-next-line no-useless-escape
            return `tw-calendar-tile`;
        }
        return null;
    };

    const handleDateClick = (value) => {
        const selectedDate = new Date(value).getTime();
        const today = new Date().getTime();
        const current = new Date(currentDate).getTime();

        if (current === selectedDate) {
            setCurrentDate(null);
            setCurrentStatus(eventStatusDict.nadchodzace);
        } else {
            setCurrentDate(value);
            if (selectedDate < today) {
                setCurrentStatus(eventStatusDict.wszystkie);
            }
        }
    };

    const changeCategory = (event) => {
        setCurrentTag(event.target.value);
    };

    const handleGroupChange = (event) => {
        const value = event.target.value;

        if (currentGroup.includes(value)) setCurrentGroup(currentGroup.filter((item) => item !== value));
        else setCurrentGroup([...currentGroup, value]);
    };

    const changeStatus = (event) => {
        setCurrentStatus(event.target.value);
    };

    const loadMore = () => {
        setCurrentPage((prev) => prev + 1);
    };

    return (
        <>
            {(loading && (
                <Oval
                    height="100"
                    width="100"
                    color={darkMode ? '#fffc00' : '#012D69'}
                    secondaryColor={darkMode ? '#fffc00' : '#012D69'}
                    ariaLabel="oval-loading"
                    wrapperClass="tw-mt-20 tw-mb-20 tw-flex tw-w-full tw-justify-center"
                />
            )) || (
                <>
                    <h1 className="tw-mt-0">Wydarzenia</h1>
                    <div className="tw-grid tw-grid-cols-1 tw-grid-rows-[auto_auto] tw-gap-4 md:tw-grid-cols-[350px_auto_auto] md:tw-grid-rows-1 md:tw-gap-8">
                        <div className="tw-flex tw-flex-col tw-gap-4">
                            <h2 className="tw-m-0 tw-text-xl">Filtry</h2>
                            <div>
                                <FormGroup initiallyVisible={true} title="Status wydarzenia">
                                    {Object.values(eventStatusDict).map((status, index) => (
                                        <FormInput
                                            key={index}
                                            label={status}
                                            type="radio"
                                            name="status"
                                            value={status}
                                            onChange={changeStatus}
                                            checked={currentStatus === status}
                                        />
                                    ))}
                                </FormGroup>
                                <FormGroup title="Typ wydarzenia">
                                    {eventTags.map((tag, index) => (
                                        <FormInput key={index} label={tag} type="radio" name="tags" value={tag} onChange={changeCategory} checked={currentTag === tag} />
                                    ))}
                                </FormGroup>
                                <FormGroup title="Grupa docelowa">
                                    {Object.keys(targetGroupsDict).map((group, index) => (
                                        <FormInput key={index} label={targetGroupsDict[group]} type="checkbox" name="groups" value={group} onChange={handleGroupChange} />
                                    ))}
                                </FormGroup>
                            </div>
                            <div>
                                <h2 className="tw-mt-0 tw-text-xl">Kalendarz</h2>
                                <Calendar
                                    className="!tw-w-full dark:!tw-bg-veryBlack dark:!tw-text-white"
                                    locale={'pl-PL'}
                                    value={currentDate}
                                    next2Label={null}
                                    prev2Label={null}
                                    view="month"
                                    onClickDay={handleDateClick}
                                    tileClassName={printCalendarClassName}
                                />
                            </div>
                        </div>
                        <div className="tw-flex tw-flex-col  md:tw-col-span-2">
                            <ResultInfo eventList={visibleEvents} currentPage={currentPage} />
                            <EventList events={visibleEvents} currentPage={currentPage} />
                            <ResultInfo eventList={visibleEvents} currentPage={currentPage} />
                            {pages > 1 && currentPage < pages && (
                                <div className="tw-mt-4 tw-flex tw-w-full tw-justify-center">
                                    <button
                                        className="tw-inline-block tw-border-2 tw-border-solid tw-border-darkBlue tw-bg-white tw-px-[15px] tw-py-2 tw-text-[12px] tw-font-medium tw-uppercase tw-text-darkBlue tw-no-underline tw-transition-colors  hover:tw-bg-darkBlue hover:tw-text-white dark:tw-border-neonYellow dark:tw-bg-veryBlack dark:tw-text-neonYellow dark:hover:tw-bg-neonYellow dark:hover:tw-text-veryBlack"
                                        onClick={loadMore}
                                    >
                                        Pokaż więcej
                                    </button>{' '}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
